import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { IStockingPlanFilterOptions } from '@app/api/stockingplan.api';
import { tableDataInit } from '@app/pages/FishingManagement/StockingPlan/conts';
import { formatFromFilter, formatToFilter } from '@app/utils/formatDate';
import { getUpdatedOptions } from '@app/utils/pageFilters';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

interface StockingPlanFilterState {
  selectedOrganizationalEntityIds: FilterIdsType;
  selectedFishtypeIds: number[];
  selectedWaterbodyIds: number[];
  fromDate: Dayjs | null | undefined;
  toDate: Dayjs | null | undefined;
  recurring: boolean | undefined;
  selectedOrganizationalEntityIdsTemporal: FilterIdsType;
  selectedFishtypeIdsTemporal: number[];
  selectedWaterbodyIdsTemporal: number[];
  fromDateTemporal: Dayjs | null | undefined;
  toDateTemporal: Dayjs | null | undefined;
  recurringTemporal: boolean | undefined;
  shouldTriggerFetch: boolean;
  filterOptions: IStockingPlanFilterOptions;
}

const initialState: StockingPlanFilterState = {
  selectedOrganizationalEntityIds: [],
  selectedFishtypeIds: [],
  selectedWaterbodyIds: [],
  fromDate: undefined,
  toDate: undefined,
  recurring: undefined,
  selectedOrganizationalEntityIdsTemporal: [],
  selectedFishtypeIdsTemporal: [],
  selectedWaterbodyIdsTemporal: [],
  fromDateTemporal: undefined,
  toDateTemporal: undefined,
  recurringTemporal: undefined,
  shouldTriggerFetch: false,
  filterOptions: tableDataInit.filterOptions,
};

const stockingPlanFiltersSlice = createSlice({
  name: 'stockingPlanFilters',
  initialState,
  reducers: {
    setSelectedOrganizationalEntityIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIds = action.payload;
    },
    setSelectedFishtypeIds(state, action: PayloadAction<number[]>) {
      state.selectedFishtypeIds = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setFromDate(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromDate = action.payload;
    },
    setToDate(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toDate = action.payload;
    },
    setRecurring(state, action: PayloadAction<boolean | undefined>) {
      state.recurring = action.payload;
    },
    setSelectedOrganizationalEntityIdsTemporal(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIdsTemporal = action.payload;
    },
    setSelectedFishtypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedFishtypeIdsTemporal = action.payload;
    },
    setSelectedWaterbodyIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIdsTemporal = action.payload;
    },
    setFromDateTemporal(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromDateTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToDateTemporal(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toDateTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    setRecurringTemporal(state, action: PayloadAction<boolean | undefined>) {
      state.recurringTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.selectedOrganizationalEntityIds = initialState.selectedOrganizationalEntityIds;
      state.selectedFishtypeIds = initialState.selectedFishtypeIds;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.fromDate = initialState.fromDate;
      state.toDate = initialState.toDate;
      state.recurring = initialState.recurring;
      state.selectedOrganizationalEntityIdsTemporal = initialState.selectedOrganizationalEntityIdsTemporal;
      state.selectedFishtypeIdsTemporal = initialState.selectedFishtypeIdsTemporal;
      state.selectedWaterbodyIdsTemporal = initialState.selectedWaterbodyIdsTemporal;
      state.fromDateTemporal = initialState.fromDateTemporal;
      state.toDateTemporal = initialState.toDateTemporal;
      state.recurringTemporal = initialState.recurringTemporal;
    },
    applyFilters(state) {
      state.selectedOrganizationalEntityIds = state.selectedOrganizationalEntityIdsTemporal;
      state.selectedFishtypeIds = state.selectedFishtypeIdsTemporal;
      state.selectedWaterbodyIds = state.selectedWaterbodyIdsTemporal;
      state.fromDate = state.fromDateTemporal;
      state.toDate = state.toDateTemporal;
      state.recurring = state.recurringTemporal;
      state.shouldTriggerFetch = true;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setFilterOptions(state, action: PayloadAction<IStockingPlanFilterOptions>) {
      const filterOptions: IStockingPlanFilterOptions = {
        ...tableDataInit.filterOptions,
        ...action.payload,
        uniqueOrganizationalEntities: action.payload.uniqueOrganizationalEntities ?? [],
        uniqueFishtypes: action.payload.uniqueFishtypes ?? [],
        uniqueWaterbodies: action.payload.uniqueWaterbodies ?? [],
      };
      state.filterOptions = filterOptions;

      const availableOrganizationalEntityIds = getUpdatedOptions(
        state.selectedOrganizationalEntityIdsTemporal ?? [],
        filterOptions.uniqueOrganizationalEntities,
      );
      state.selectedOrganizationalEntityIdsTemporal = availableOrganizationalEntityIds;
      state.selectedOrganizationalEntityIds = availableOrganizationalEntityIds;

      const availableFishtypeIds = getUpdatedOptions(
        state.selectedFishtypeIdsTemporal ?? [],
        filterOptions.uniqueFishtypes,
      );
      state.selectedFishtypeIdsTemporal = availableFishtypeIds as number[];
      state.selectedFishtypeIds = availableFishtypeIds as number[];

      const availableWaterbodyIds = getUpdatedOptions(
        state.selectedWaterbodyIdsTemporal ?? [],
        filterOptions.uniqueWaterbodies,
      );
      state.selectedWaterbodyIdsTemporal = availableWaterbodyIds as number[];
      state.selectedWaterbodyIds = availableWaterbodyIds as number[];
    },
  },
});

export const {
  setSelectedOrganizationalEntityIds,
  setSelectedFishtypeIds,
  setSelectedWaterbodyIds,
  setFromDate,
  setToDate,
  setRecurring,
  setSelectedOrganizationalEntityIdsTemporal,
  setSelectedFishtypeIdsTemporal,
  setSelectedWaterbodyIdsTemporal,
  setFromDateTemporal,
  setToDateTemporal,
  setRecurringTemporal,
  resetFilters,
  applyFilters,
  setShouldTriggerFetch,
  setFilterOptions,
} = stockingPlanFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { stockingPlanFilters: StockingPlanFilterState }) => {
  const { selectedOrganizationalEntityIds, selectedFishtypeIds, selectedWaterbodyIds, fromDate, toDate, recurring } =
    state.stockingPlanFilters;

  return (
    selectedOrganizationalEntityIds.length > 0 ||
    selectedFishtypeIds.length > 0 ||
    selectedWaterbodyIds.length > 0 ||
    !!fromDate ||
    !!toDate ||
    recurring !== undefined
  );
};

export const activeFiltersCountSelector = (state: { stockingPlanFilters: StockingPlanFilterState }) => {
  const { selectedOrganizationalEntityIds, selectedFishtypeIds, selectedWaterbodyIds, fromDate, toDate, recurring } =
    state.stockingPlanFilters;

  let count = 0;
  if (selectedOrganizationalEntityIds.length > 0) count++;
  if (selectedFishtypeIds.length > 0) count++;
  if (selectedWaterbodyIds.length > 0) count++;
  if (fromDate) count++;
  if (toDate) count++;
  if (recurring !== undefined) count++;

  return count;
};

export const fetchParamsSelector = (state: { stockingPlanFilters: StockingPlanFilterState }) => {
  const {
    selectedOrganizationalEntityIdsTemporal,
    selectedFishtypeIdsTemporal,
    selectedWaterbodyIdsTemporal,
    fromDateTemporal,
    toDateTemporal,
    recurringTemporal,
  } = state.stockingPlanFilters;

  const params: {
    organizationalEntityIds?: number[];
    fishtypeIds?: number[];
    waterbodyIds?: number[];
    start?: string;
    end?: string;
    recurring?: boolean;
  } = {};

  if (selectedOrganizationalEntityIdsTemporal.length > 0) {
    params.organizationalEntityIds = selectedOrganizationalEntityIdsTemporal as number[];
  }

  if (selectedFishtypeIdsTemporal.length > 0) {
    params.fishtypeIds = selectedFishtypeIdsTemporal;
  }

  if (selectedWaterbodyIdsTemporal.length > 0) {
    params.waterbodyIds = selectedWaterbodyIdsTemporal;
  }

  if (fromDateTemporal) {
    params.start = formatFromFilter(fromDateTemporal);
  }

  if (toDateTemporal) {
    params.end = formatToFilter(toDateTemporal);
  }

  if (recurringTemporal !== undefined) {
    params.recurring = recurringTemporal;
  }

  return params;
};

export const stockingPlanFiltersActions = stockingPlanFiltersSlice.actions;

export const stockingPlanFilters = stockingPlanFiltersSlice.reducer;
